import { api } from './api';
import { Device } from '@capacitor/device';
import { DeviceDto } from '@/types/device';
import { VERSION } from '@/constant/version';
import OneSignal from 'onesignal-cordova-plugin';
import { getProId, isLoggedIn } from '@/services/auth';
import { Capacitor } from '@capacitor/core';
import { DateTime } from 'luxon';

const BASE_URL = '/cms/devices';

let _updateDeviceInProgress = false;

export async function updateDevice(): Promise<void> {
  if (_updateDeviceInProgress) return;
  if (!(await isLoggedIn())) return;

  _updateDeviceInProgress = true;

  try {
    const proId = await getProId();

    if (!proId) {
      throw new Error('Pro ID not found after sign in');
    }

    const info = await Device.getInfo();

    let uuid: string;
    let token: string | null = null;

    if (Capacitor.isNativePlatform()) {
      uuid = await OneSignal.User.pushSubscription.getIdAsync();
      token = await OneSignal.User.pushSubscription.getTokenAsync();

      if (!uuid) {
        console.warn('Could not get UUID from OneSignal');
        return;
      }
    } else {
      uuid = new Date().toISOString();
      token = new Date().toISOString();
    }

    const deviceDto: DeviceDto = {
      uuid,
      token,
      platform: info.platform,
      version: info.osVersion,
      model: info.model,
      manufacturer: info.manufacturer,
      app_version: VERSION,
      pro_id: proId,
      updated_at: DateTime.now(),
    };

    const device = (await getDeviceByUUID(deviceDto.uuid)) ?? (await getDeviceByToken(token));

    if (device) {
      await api.put(`${BASE_URL}/${device.id}`, deviceDto);
    } else {
      try {
        await api.post(BASE_URL, deviceDto);
      } catch (err: any) {
        // Dernier recours : race condition entre deux appels simultanés
        if (err?.response?.status === 500) {
          const existing = await getDeviceByToken(token);
          if (existing) {
            await api.put(`${BASE_URL}/${existing.id}`, deviceDto);
          }
        }
      }
    }
  } finally {
    _updateDeviceInProgress = false;
  }
}

export async function getDeviceByUUID(uuid: string): Promise<DeviceDto | null> {
  const proId = await getProId();

  if (!proId) {
    return null;
  }

  const response = await api.get(BASE_URL, { params: { uuid } });
  return response.data?.items?.[0] ?? null;
}

async function getDeviceByToken(token: string | null): Promise<DeviceDto | null> {
  if (!token) return null;

  const response = await api.get(BASE_URL, { params: { token } });
  return response.data?.items?.[0] ?? null;
}
